/**
 * @file
 * The Accordion class.
 */

import AccordionItem from "./AccordionItem.js";
import { addClass, removeClass } from "../domHelpers.js";
import { keyPress, preventEvent } from "../eventHandlers.js";
import { isValidType, isValidClassList } from "../validate.js";
import Component from "../Component.js";

class Accordion extends Component {
  /**
   * The DOM elements within the accordion.
   *
   * @protected
   *
   * @type {Object<HTMLElement, HTMLElement[]>}
   *
   * @property {HTMLElement}   accordion                  - The accordion element.
   * @property {HTMLElement[]} accordionItems             - An array of accordion items.
   * @property {HTMLElement[]} accordionItemToggles       - An array of accordion item toggles.
   * @property {HTMLElement[]} accordionItemHeaders       - An array of accordion headers.
   * @property {HTMLElement[]} accordionItemContents      - An array of accordion item contents.
   * @property {HTMLElement}   accordionControlContainer  - The accordion control container.
   * @property {HTMLElement[]} accordionControls          - The accordion controls.
   * @property {HTMLElement[]} expandController           - The expand all controller(s).
   * @property {HTMLElement[]} collapseController         - The collapse all controller(s).
   */
  _dom = {
    accordion: null,
    accordionItems: [],
    accordionItemToggles: [],
    accordionItemHeaders: [],
    accordionItemContents: [],
    accordionControlContainer: null,
    accordionControls: [],
    expandController: [],
    collapseController: [],
  };

  /**
   * The DOM elements within the accordion that cannot be reset or generated by the accordion itself.
   *
   * @protected
   *
   * @type {string[]}
   */
  _protectedDOMElements = ["accordion"];

  /**
   * The query selectors used by the accordion.
   *
   * @protected
   *
   * @type {Object<string>}
   *
   * @property {string} accordionItems            - The query selector for accordion items.
   * @property {string} accordionItemToggles      - The query selector for accordion toggles.
   * @property {string} accordionItemHeaders      - The query selector for accordion headers.
   * @property {string} accordionItemContents     - The query selector for accordion contents.
   * @property {string} accordionControlContainer - The querySelector for the accordion control container.
   * @property {string} accordionControls         - The query selector string for accordion controls.
   * @property {string} expandController          - The query selector string for the expand all controller(s).
   * @property {string} collapseController        - The query selector string for the collapse all controller(s).
   */
  _selectors = {
    accordionItems: "",
    accordionItemToggles: "",
    accordionItemHeaders: "",
    accordionItemContents: "",
    accordionControlContainer: "",
    accordionControls: "",
    expandController: "",
    collapseController: "",
  };

  /**
   * The list of accordion items.
   *
   * @protected
   *
   * @type {Object<AccordionItem[]>}
   *
   * @property {AccordionItem[]} accordionItems - The list of accordion items.
   */
  _elements = {
    accordionItems: [],
  };

  /**
   * The classes to apply when the accordions are in various states.
   *
   * @protected
   *
   * @type {Object<string, string[]>}
   *
   * @property {string|string[]} open         - The class(es) to apply when the accordion item is open.
   * @property {string|string[]} close        - The class(es) to apply when the accordion item is closed.
   * @property {string|string[]} transition   - The class(es) to apply when the accordion item is transitioning between states.
   * @property {string|string[]} initialize   - The class(es) to apply when the accordion item is initializing.
   */
  _classes = {
    open: "show",
    close: "hide",
    transition: "transitioning",
    initialize: "initializing",
  };

  /**
   * The duration times (in milliseconds) for various things throughout the accordions.
   *
   * @protected
   *
   * @type {Object<number>}
   *
   * @property {number} transition - The duration time (in milliseconds) for the transition between open and closed states.
   * @property {number} open       - The duration time (in milliseconds) for the transition from closed to open states.
   * @property {number} close      - The duration time (in milliseconds) for the transition from open to closed states.
   */
  _durations = {
    transition: 250,
    open: -1,
    close: -1,
  };

  /**
   * A flag to decide if the accordion items can be navigated by arrows.
   *
   * @protected
   *
   * @type {boolean}
   */
  _optionalKeySupport = true;

  /**
   * A flag to decide if multiple accordions can be open at the same time.
   *
   * If set to false, only one accordion can be open at a time.
   *
   * @protected
   *
   * @type {boolean}
   */
  _allowMultipleExpand = true;

  /**
   * A flag to decide if no accordions can be opened at the same time.
   *
   * If set to false, at least one accordion must be open at all times.
   *
   * @protected
   *
   * @type {boolean}
   */
  _allowNoExpand = true;

  /**
   * The index of the current child node.
   *
   * @protected
   *
   * @type {number}
   */
  _currentChild = 0;

  /**
   * The key used for storage.
   *
   * @protected
   *
   * @type {string}
   */
  _storageKey = "accordions";

  /**
   * Constructs a new `Accordion`.
   *
   * @param {object}             options                                                                    - The options for generating the accordion.
   * @param {HTMLElement}        [options.accordionElement]                                                 - The accordion element in the DOM.
   * @param {string}             [options.accordionItemSelector = .accordion-item]                          - The query selector string for accordion items.
   * @param {string}             [options.accordionItemToggleSelector = .accordion-item-toggle]             - The query selector string for accordion toggle.
   * @param {string}             [options.accordionItemHeaderSelector = .accordion-item-header]             - The query selector string for accordion header.
   * @param {string}             [options.accordionItemContentSelector = .accordion-item-content]           - The query selector string for accordion content.
   * @param {string}             [options.accordionControlContainerSelector = .accordion-control-container] - The query selector string for the accordion control container.
   * @param {string}             [options.accordionControlsSelector = .accordion-control]                   - The query selector string for accordion controls.
   * @param {string}             [options.expandControllerSelector = .expand-all]                           - The query selector string for the expand all controller(s).
   * @param {string}             [options.collapseControllerSelector = .collapse-all]                       - The query selector string for the collapse all controller(s).
   * @param {?(string|string[])} [options.openClass = show]                                                 - The class to apply when a accordion is "open".
   * @param {?(string|string[])} [options.closeClass = hide]                                                - The class to apply when a accordion is "closed".
   * @param {?(string|string[])} [options.transitionClass = transitioning]                                  - The class to apply when a accordion is transitioning between "open" and "closed" states.
   * @param {number}             [options.transitionDuration = 300]                                         - The duration of the transition between "open" and "closed" states (in milliseconds).
   * @param {number}             [options.openDuration = -1]                                                - The duration of the transition from "closed" to "open" states (in milliseconds).
   * @param {number}             [options.closeDuration = -1]                                               - The duration of the transition from "open" to "closed" states (in milliseconds).
   * @param {boolean}            [options.optionalKeySupport = false]                                       - A flag to determine if accordions can be navigated with arrows.
   * @param {boolean}            [options.allowMultipleExpand = true]                                       - A flag to determine if multiple accordions can be open at the same time.
   * @param {boolean}            [options.allowNoExpand = true]                                             - A flag to determine if no accordions can be open at the same time.
   * @param {boolean}            [options.automaticActivation = false]                                      - A flag to set if focusing a accordion item toggle will automatically activate it.
   * @param {?string}            [options.prefix = graupl-]                                                 - The prefix used for CSS custom properties and attributes.
   * @param {?string}            [options.key = null]                                                       - The key used to generate IDs throughout the accordion.
   * @param {?(string|string[])} [options.initializeClass = initializing]                                   - The class(es) to apply when the accordion is initializing.
   * @param {boolean}            [options.initialize = false]                                               - A flag to initialize the accordion immediately upon creation.
   */
  constructor({
    accordionElement,
    accordionItemSelector = ".accordion-item",
    accordionItemToggleSelector = ".accordion-item-toggle",
    accordionItemHeaderSelector = ".accordion-item-header",
    accordionItemContentSelector = ".accordion-item-content",
    accordionControlContainerSelector = ".accordion-control-container",
    accordionControlsSelector = ".accordion-control",
    expandControllerSelector = ".expand-all",
    collapseControllerSelector = ".collapse-all",
    openClass = "show",
    closeClass = "hide",
    transitionClass = "transitioning",
    transitionDuration = 300,
    openDuration = -1,
    closeDuration = -1,
    optionalKeySupport = false,
    allowMultipleExpand = true,
    allowNoExpand = true,
    automaticActivation = false,
    prefix = "graupl-",
    key = null,
    initializeClass = "initializing",
    initialize = false,
  }) {
    super({
      prefix,
      key,
      initializeClass,
    });

    // Set DOM elements.
    this._dom.accordion = accordionElement;

    // Set the query selectors.
    this._selectors.accordionItems = accordionItemSelector;
    this._selectors.accordionItemToggles = accordionItemToggleSelector;
    this._selectors.accordionItemHeaders = accordionItemHeaderSelector;
    this._selectors.accordionItemContents = accordionItemContentSelector;
    this._selectors.accordionControlContainer =
      accordionControlContainerSelector;
    this._selectors.accordionControls = accordionControlsSelector;
    this._selectors.expandController = expandControllerSelector;
    this._selectors.collapseController = collapseControllerSelector;

    // Set the classes.
    this._classes.open = openClass || "";
    this._classes.close = closeClass || "";
    this._classes.transition = transitionClass || "";

    // Set the durations.
    this._durations.transition = transitionDuration;
    this._durations.open = openDuration;
    this._durations.close = closeDuration;

    // Set automatic activation.
    this._automatic = automaticActivation;

    // Set optional key support.
    this._optionalKeySupport = optionalKeySupport;

    // Set expand rules.
    this._allowMultipleExpand = allowMultipleExpand;
    this._allowNoExpand = allowNoExpand;

    if (initialize) {
      this.initialize();
    }
  }

  /**
   * Initializes the accordion.
   */
  initialize() {
    try {
      // Add the initialization class.
      addClass(this._classes.initialize, this.dom.accordion);

      super.initialize();

      // Set up the DOM.
      this._setDOMElements();
      this._setIds();
      this._setCustomProps();

      // Create the child elements.
      this._createChildElements();

      // Handle events.
      this._handleFocus();
      this._handleClick();
      this._handleKeydown();
      this._handleKeyup();

      // Store the component.
      this._store();

      // Handle enabling/disabling controls based on options.
      if (this.dom.expandController.length > 0 && !this.allowMultipleExpand) {
        this.dom.expandController.forEach((control) => {
          control.setAttribute("disabled", "disabled");
        });
      }
      if (this.dom.collapseController.length > 0 && !this.allowNoExpand) {
        this.dom.collapseController.forEach((control) => {
          control.setAttribute("disabled", "disabled");
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      // Remove the initialization class.
      requestAnimationFrame(() => {
        removeClass(this._classes.initialize, this.dom.accordion);
      });
    }
  }

  /**
   * The class(es) to apply when the accordion is open.
   *
   * @type {string|string[]}
   *
   * @see _classes.open
   */
  get openClass() {
    return this._classes.open;
  }

  set openClass(value) {
    isValidClassList({ openClass: value });

    if (this._classes.open !== value) {
      this._classes.open = value;
    }
  }

  /**
   * The class(es) to apply when the accordion is closed.
   *
   * @type {string|string[]}
   *
   * @see _classes.close
   */
  get closeClass() {
    return this._classes.close;
  }

  set closeClass(value) {
    isValidClassList({ closeClass: value });

    if (this._classes.close !== value) {
      this._classes.close = value;
    }
  }

  /**
   * The class(es) to apply when the accordion is transitioning between open and closed.
   *
   * @type {string|string[]}
   *
   * @see _classes.transition
   */
  get transitionClass() {
    return this._classes.transition;
  }

  set transitionClass(value) {
    isValidClassList({ transitionClass: value });

    if (this._classes.transition !== value) {
      this._classes.transition = value;
    }
  }

  /**
   * The duration time (in milliseconds) for the transition between open and closed states.
   *
   * @type {number}
   *
   * @see _durations.transition
   */
  get transitionDuration() {
    return this._durations.transition;
  }

  set transitionDuration(value) {
    isValidType("number", { transitionDuration: value });

    if (this._durations.transition !== value) {
      this._durations.transition = value;
      this._setTransitionDurations();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from closed to open states.
   *
   * If openDuration is set to -1, the transitionDuration will be used instead.
   *
   * @type {number}
   *
   * @see _durations.open
   */
  get openDuration() {
    return this._durations.open === -1
      ? this.transitionDuration
      : this._durations.open;
  }

  set openDuration(value) {
    isValidType("number", { openDuration: value });

    if (this._durations.open !== value) {
      this._durations.open = value;
      this._setTransitionDurations();
    }
  }

  /**
   * The duration time (in milliseconds) for the transition from open to closed states.
   *
   * If closeDuration is set to -1, the transitionDuration will be used instead.
   *
   * @type {number}
   *
   * @see _durations.close
   */
  get closeDuration() {
    return this._durations.close === -1
      ? this.transitionDuration
      : this._durations.close;
  }

  set closeDuration(value) {
    isValidType("number", { closeDuration: value });

    if (this._durations.close !== value) {
      this._durations.close = value;
      this._setTransitionDurations();
    }
  }

  /**
   * The current index of the accordion item.
   *
   * @readonly
   *
   * @type {number}
   *
   * @see _currentChild
   */
  get currentChild() {
    return this._currentChild;
  }

  set currentChild(value) {
    isValidType("number", { currentChild: value });

    if (
      this._currentChild !== value &&
      value >= 0 &&
      value < this.elements.accordionItems.length
    ) {
      this._currentChild = value;
    }
  }

  /**
   * A flag to decide if the accordion items can be navigated by arrows.
   *
   * @type {boolean}
   *
   * @see _optionalKeySupport
   */
  get optionalKeySupport() {
    return this._optionalKeySupport;
  }

  set optionalKeySupport(value) {
    isValidType("boolean", { optionalKeySupport: value });

    if (this._optionalKeySupport !== value) {
      this._optionalKeySupport = value;
    }
  }

  /**
   * The currently selected accordion item.
   *
   * @readonly
   *
   * @type {AccordionItem}
   */
  get currentAccordionItem() {
    return this.elements.accordionItems[this.currentChild];
  }

  /**
   * The currently open accordion items.
   *
   * @readonly
   *
   * @type {AccordionItem[]}
   */
  get openAccordionItems() {
    return this.elements.accordionItems.filter((item) => item.isOpen);
  }

  /**
   * A flag to decide if multiple accordions can be open at the same time.
   *
   * @type {boolean}
   *
   * @see _allowMultipleExpand
   */
  get allowMultipleExpand() {
    return this._allowMultipleExpand;
  }

  set allowMultipleExpand(value) {
    isValidType("boolean", { allowMultipleExpand: value });

    if (this._allowMultipleExpand !== value) {
      this._allowMultipleExpand = value;

      if (this.dom.expandController.length > 0) {
        if (value) {
          this.dom.expandController.forEach((control) => {
            control.removeAttribute("disabled");
          });
        } else {
          this.dom.expandController.forEach((control) => {
            control.setAttribute("disabled", "disabled");
          });
        }
      }
    }
  }

  /**
   * A flag to decide if no accordions can be opened at the same time.
   *
   * @type {boolean}
   *
   * @see _allowNoExpand
   */
  get allowNoExpand() {
    return this._allowNoExpand;
  }

  set allowNoExpand(value) {
    isValidType("boolean", { allowNoExpand: value });

    if (this._allowNoExpand !== value) {
      this._allowNoExpand = value;

      if (this.dom.collapseController.length > 0) {
        if (value) {
          this.dom.collapseController.forEach((control) => {
            control.removeAttribute("disabled");
          });
        } else {
          this.dom.collapseController.forEach((control) => {
            control.setAttribute("disabled", "disabled");
          });
        }
      }
    }
  }

  /**
   * Validates all aspects of the accordion item to ensure proper functionality.
   *
   * @protected
   *
   * @return {boolean} - The result of the validation.
   */
  _validate() {
    super._validate();

    // Boolean checks.
    const booleans = {
      automaticActivation: this._automatic,
      optionalKeySupport: this._optionalKeySupport,
      allowMultipleExpand: this._allowMultipleExpand,
      allowNoExpand: this._allowNoExpand,
    };

    // Check the booleans.
    const booleanChecks = isValidType("boolean", booleans);

    // Handle boolean check failure.
    if (!booleanChecks) {
      this._errors.push(booleanChecks.message);
      this._valid = false;
    }

    return this._valid;
  }

  /**
   * Sets the IDs of the accordion and it's children if they do not already exist.
   *
   * The generated IDs use the key and follow the format:
   *  - accordion: `accordion-${key}`
   *
   * @protected
   */
  _setIds() {
    this.dom.accordion.id = this.dom.accordion.id || `accordion-${this.key}`;

    this._id = this.dom.accordion.id;
  }

  /**
   * Sets custom props throughout the accordion.
   *
   * The custom properties are:
   *   - `--graupl-accordion-transition-duration`,
   *   - `--graupl-accordion-open-transition-duration`, and
   *   - `--graupl-accordion-close-transition-duration`.
   *
   * The prefix of `graupl-` can be changed by setting the accordion's prefix value.
   *
   * @protected
   */
  _setCustomProps() {
    this.dom.accordion.style.setProperty(
      `--${this.prefix}accordion-transition-duration`,
      `${this.transitionDuration}ms`
    );

    this.dom.accordion.style.setProperty(
      `--${this.prefix}accordion-open-transition-duration`,
      `${this.openDuration}ms`
    );

    this.dom.accordion.style.setProperty(
      `--${this.prefix}accordion-close-transition-duration`,
      `${this.closeDuration}ms`
    );
  }

  /**
   * Sets all DOM elements within the accordion.
   *
   * Utilizes _setDOMElementType and
   * _resetDOMElementType.
   *
   * @protected
   */
  _setDOMElements() {
    this._setDOMElementType("accordionItems", { context: this.dom.accordion });
    this._resetDOMElementType("accordionItemToggles");
    this._setDOMElementType("accordionControlContainer", {
      context: this.dom.accordion,
    });

    if (this.dom.accordionControlContainer) {
      this._setDOMElementType("accordionControls", {
        context: this.dom.accordionControlContainer,
      });
      this._setDOMElementType("expandController", {
        context: this.dom.accordionControlContainer,
      });
      this._setDOMElementType("collapseController", {
        context: this.dom.accordionControlContainer,
      });
    }

    this.dom.accordionItems.forEach((accordionItem) => {
      this._setDOMElementType("accordionItemToggles", {
        context: accordionItem,
        overwrite: false,
        strict: false,
      });
      this._setDOMElementType("accordionItemHeaders", {
        context: accordionItem,
        overwrite: false,
        strict: false,
      });
      this._setDOMElementType("accordionItemContents", {
        context: accordionItem,
        overwrite: false,
        strict: false,
      });
    });
  }

  /**
   * Creates and initializes all accordion items.
   *
   * @protected
   */
  _createChildElements() {
    this.dom.accordionItems.forEach((accordionItem, index) => {
      const item = new AccordionItem({
        accordionItemElement: accordionItem,
        accordionItemToggleElement: this.dom.accordionItemToggles[index],
        accordionItemHeaderElement: this.dom.accordionItemHeaders[index],
        accordionItemContentElement: this.dom.accordionItemContents[index],
        parentAccordion: this,
      });

      item.initialize();

      this.elements.accordionItems.push(item);
    });
  }

  /**
   * Handles focus events throughout the accordion for proper use.
   *
   * - Adds a `focus` listener to every accordion item so when it gains focus,
   *   it will set the accordion's current child to the index of the item.
   *
   * @protected
   */
  _handleFocus() {
    this.elements.accordionItems.forEach((accordionItem, index) => {
      this._addEventListener("focus", accordionItem.dom.toggle, () => {
        this.currentChild = index;
      });
    });
  }

  /**
   * Handles click events throughout the accordion item for proper use.
   *
   * - Adds a `pointerup` listener to the accordion item toggles that will toggle each accordion item.
   *
   * @protected
   */
  _handleClick() {
    this.elements.accordionItems.forEach((accordionItem, index) => {
      this._addEventListener("pointerup", accordionItem.dom.toggle, () => {
        this.currentChild = index;
        this.currentEvent = "mouse";
        accordionItem.toggle();
      });
    });

    // Clicks for the accordion controls.
    this.dom.expandController.forEach((control) => {
      this._addEventListener("pointerup", control, () => {
        this.currentEvent = "mouse";
        if (this.allowMultipleExpand) {
          this.openChildren();
        }
      });
    });
    this.dom.collapseController.forEach((control) => {
      this._addEventListener("pointerup", control, () => {
        this.currentEvent = "mouse";
        if (this.allowNoExpand) {
          this.closeChildren();
        }
      });
    });
  }

  /**
   * Handles keydown events throughout the accordion item for proper use.
   *
   * This method exists to assist the _handleKeyup method.
   *
   * - Adds a `keydown` listener to all accordion item toggles.
   *   - Blocks propagation on "Space" and "Enter" keys.
   *   - _If_ optionalKeySupport is enabled, blocks propagation on the following keys:
   *     - "ArrowDown", "ArrowUp", "Home", and "End".
   * - Adds a `keydown` listener to all accordion controls.
   *   - Blocks propagation on "Space" and "Enter" keys.
   */
  _handleKeydown() {
    this.dom.accordionItemToggles.forEach((accordionToggle) => {
      this._addEventListener("keydown", accordionToggle, (event) => {
        const key = keyPress(event);
        const toggleKeys = ["Space", "Enter"];

        if (toggleKeys.includes(key)) {
          preventEvent(event);
          this.currentEvent = "keyboard";
        } else if (this.optionalKeySupport) {
          const optionalKeys = ["ArrowDown", "ArrowUp", "Home", "End"];

          if (optionalKeys.includes(key)) {
            preventEvent(event);
            this.currentEvent = "keyboard";
          }
        }
      });
    });

    this.dom.accordionControls.forEach((control) => {
      this._addEventListener("keydown", control, (event) => {
        const key = keyPress(event);
        const controllerKeys = ["Space", "Enter"];

        if (controllerKeys.includes(key)) {
          preventEvent(event);
          this.currentEvent = "keyboard";
        }
      });
    });
  }

  /**
   * Handles keyup events throughout the accordion item for proper use.
   *
   * Adds the follow keybindings (explanations are taken from the WAI ARIA Practices Guide Accordion Pattern):
   *
   * - `Enter` or `Space`:
   *   - When focus is on the accordion header for a collapsed panel, expands the associated panel. If the implementation allows only one panel to be expanded, and if another panel is expanded, collapses that panel.
   *   - When focus is on the accordion header for an expanded panel, collapses the panel if the implementation supports collapsing. Some implementations require one panel to be expanded at all times and allow only one panel to be expanded; so, they do not support a collapse function.
   *   - When focus is on the accordion expand all control, expands all panels. If the implementation allows only one panel to be expanded, does nothing.
   *   - WHen focus is on the accordion collapse all control, collapses all panels. If the implementation requires one panel to be expanded at all times, does nothing.
   * - `Accordion`: Moves focus to the next focusable element; all focusable elements in the accordion are included in the page `Accordion` sequence.
   * - `Shift + Accordion`: Moves focus to the previous focusable element; all focusable elements in the accordion are included in the page `Accordion` sequence.
   * - `Down Arrow` (Optional): If focus is on an accordion header, moves focus to the next accordion header. If focus is on the last accordion header, either does nothing or moves focus to the first accordion header.
   * - `Up Arrow` (Optional): If focus is on an accordion header, moves focus to the previous accordion header. If focus is on the first accordion header, either does nothing or moves focus to the last accordion header.
   * - `Home` (Optional): When focus is on an accordion header, moves focus to the first accordion header.
   * - `End` (Optional): When focus is on an accordion header, moves focus to the last accordion header.
   *
   * Note: When the above explanations mention "accordion header", they are referring to the accordion item toggle.
   */
  _handleKeyup() {
    this.dom.accordionItemToggles.forEach((accordionToggle) => {
      this._addEventListener("keyup", accordionToggle, (event) => {
        const key = keyPress(event);

        switch (key) {
          case "Space":
          case "Enter":
            preventEvent(event);
            this.currentEvent = "keyboard";
            this.currentAccordionItem.toggle();

            break;
        }

        if (this.optionalKeySupport) {
          switch (key) {
            case "Home":
              preventEvent(event);
              this.focusFirstChild();

              break;
            case "End":
              preventEvent(event);
              this.focusLastChild();

              break;
            case "ArrowDown":
              preventEvent(event);
              this.focusNextChild();

              break;
            case "ArrowUp":
              preventEvent(event);
              this.focusPreviousChild();

              break;
          }
        }
      });
    });

    this.dom.expandController.forEach((control) => {
      this._addEventListener("keyup", control, (event) => {
        const key = keyPress(event);

        switch (key) {
          case "Space":
          case "Enter":
            preventEvent(event);
            this.currentEvent = "keyboard";

            if (this.allowMultipleExpand) {
              this.openChildren();
            }
        }
      });
    });

    this.dom.collapseController.forEach((control) => {
      this._addEventListener("keyup", control, (event) => {
        const key = keyPress(event);

        switch (key) {
          case "Space":
          case "Enter":
            preventEvent(event);
            this.currentEvent = "keyboard";

            if (this.allowNoExpand) {
              this.closeChildren();
            }
        }
      });
    });
  }

  /**
   * Focus the accordion's current child.
   */
  focusCurrentChild() {
    if (this.currentChild !== -1) {
      this.currentAccordionItem.focus();
    }
  }

  /**
   * Focuses the accordion's child at a given index.
   *
   * @param {number} index - The index of the child to focus.
   */
  focusChild(index) {
    this.blurCurrentChild();
    this.currentChild = index;
    this.focusCurrentChild();
  }

  /**
   * Focuses the accordion's first child.
   */
  focusFirstChild() {
    this.focusChild(0);
  }

  /**
   * Focus the accordion's last child.
   */
  focusLastChild() {
    this.focusChild(this.elements.accordionItems.length - 1);
  }

  /**
   * Focus the accordion's next child.
   */
  focusNextChild() {
    if (this.currentChild < this.elements.accordionItems.length - 1) {
      this.focusChild(this.currentChild + 1);
    } else {
      this.focusCurrentChild();
    }
  }

  /**
   * Focus the accordion's previous child.
   */
  focusPreviousChild() {
    if (this.currentChild > 0) {
      this.focusChild(this.currentChild - 1);
    } else {
      this.focusCurrentChild();
    }
  }

  /**
   * Blurs the accordion's current child.
   */
  blurCurrentChild() {
    if (this.currentChild !== -1) {
      this.currentAccordionItem.blur();
    }
  }

  /**
   * Open all accordion items.
   */
  openChildren() {
    this.elements.accordionItems.forEach((item) => item.show());
  }

  /**
   * Close all accordion items.
   */
  closeChildren() {
    this.elements.accordionItems.forEach((item) => item.hide());
  }
}

export default Accordion;
